<script setup>
import {ref, onMounted, watch, nextTick, toRef} from "vue";
import { useVuelidate } from "@vuelidate/core";
import { UseCourierStore } from "@/stores/courierStore";
import { useReturnStore } from "@/stores/returnStore";
import { UseProductStore} from "@/stores/productStore";
import {useOrderDataStore} from "@/stores/orderDataStore";
import GlobalHeader from "@/components/GlobalHeader.vue";
import {useRouter} from "vue-router";
import {VueSpinner} from "vue3-spinners";
import {useScrollToError} from "@/composables/scrollToError";
import ProductItem from "@/components/ProductItem.vue";
import {useCouriersValidationCY} from "@/composables/couriersValidationCY";
import DescriptionHeader from "@/components/DescriptionHeader.vue";
import {getCourierData} from "@/service/courierService";
import PriceFormatter from "@/components/PriceFormatter.vue";
import {useI18n} from "vue-i18n";
import PostalCodeInput from "@/components/PostalCodeInput/PostalCodeInput.vue";
import PhoneInput from "@/components/PhoneInput/PhoneInput.vue";

const {t} = useI18n();

const courierStore = UseCourierStore();
const productStore = UseProductStore();
const returnStore = useReturnStore();
const orderDataStore = useOrderDataStore();
const router = useRouter();
const editingAddress = ref(null);
const saveMessage = ref(false);
const showPopup = ref(false);
const loadingCouriers = ref(true); // while fetching couriers from API
const loadingAddress = ref(true); // while fetching couriers from API
const returnWays = ref(null);
const noReturnMessage = ref(false);
const invalidPostCodeNewACS = ref(false)
const invalidPostCodeEditedACS = ref(false)
const invalidPhoneNewACS = ref(false);
const invalidPhoneEditedACS = ref(false);

onMounted(async () => {

  returnWays.value  = await getCourierData();
  console.log("return ways: ", returnWays.value)
  loadingCouriers.value = false;
  loadingAddress.value = false;

  courierStore.setDefaultRmaDeliveryAddress(); // set default rma address

});

// Vuelidate validations
const { rules } = useCouriersValidationCY();
const v$ = useVuelidate(rules, { selectedCourier: toRef(courierStore, 'selectedCourier'), selectedAddress: toRef(courierStore, 'selectedAddress'), formData: courierStore.formData, formData_backup: courierStore.formData_backup });


// Methods
const goToProductsPage = () => {
  router.push("/products");
}


const goToSummaryPage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    useScrollToError();
    return;
  }
  if (courierStore.selectedAddress === 'new-address' && courierStore.selectedCourier === 'ACS CY Express' && courierStore.showInfo === false) {
    saveMessage.value = true;
    await nextTick();  // Wait for the DOM update
    useScrollToError();
    return;
  }


  if(courierStore.editInfo === true && courierStore.saveEdit===false){
    saveMessage.value = true;
    await nextTick();  // Wait for the DOM update
    useScrollToError();
    return;
  }

  if(returnStore.finalReturn < 0) {
    noReturnMessage.value = true
    await nextTick();  // Wait for the DOM update
    useScrollToError();
    return;
  }

  router.push("/summary");
}

const displayInfo = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid || invalidPostCodeNewACS.value || invalidPhoneNewACS.value) return;

  Object.assign(courierStore.formData_backup, JSON.parse(JSON.stringify(courierStore.formData)));
  courierStore.showInfo = true;
  saveMessage.value = false;
  courierStore.selectedAddress = "edited-address";
  courierStore.formData = {name: null, phone: null, email: null, address: null, city: null, zip: null, comments: null};
}

const editForm = (addressId) => {
  if(courierStore.selectedCourier === 'ACS CY Express') {
    editingAddress.value = addressId;
    courierStore.editInfo = true;
    courierStore.saveEdit = false;
  }
}

const deleteData = () => {
  showPopup.value = false
  courierStore.deleteAddress=true;
  courierStore.showInfo = false;
  courierStore.formData_backup = {id:null, name: null, phone: null, email: null, address: null, city: null, zip: null, comments: null, country: null};
  courierStore.rmaDeliveryAddress = {id:null, name: null, phone: null, email: null, address: null, city: null, zip: null, country: null};
  courierStore.selectedAddress = null;
}

const displayEditedInfo = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid || invalidPostCodeEditedACS.value || invalidPhoneEditedACS.value) return;

  courierStore.showInfo = true;
  courierStore.saveEdit = true;
  saveMessage.value = false;
  editingAddress.value = null;
  courierStore.selectedAddress = "edited-address";
}

const courierChanged = (way) => {
  courierStore.setSelectedCourierDesc(way.description);
  courierStore.setSelectedCourierReturnCost(way.returnCost);
};


watch(() => {
  if (courierStore.selectedAddress === "new-address") {
    courierStore.showInfo = false;
  }
});

watch(showPopup, (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden'
  } else {
    document.documentElement.style.overflow = 'auto'
  }
});

watch(() => courierStore.selectedAddress, (newVal) => {
  if (courierStore.selectedCourier === "ACS CY Express") {
    if (newVal === 'old-address') {
      courierStore.rmaDeliveryAddress = {
        id: orderDataStore.deliveryAddress.id,
        name: orderDataStore.deliveryAddress.name,
        address: orderDataStore.deliveryAddress.address,
        city: orderDataStore.deliveryAddress.city,
        phone: orderDataStore.deliveryAddress.phone,
        email: orderDataStore.deliveryAddress.email,
        zip: orderDataStore.deliveryAddress.zip,
        country: orderDataStore.deliveryAddress.country
      }
    } else if (newVal === 'edited-address') {
      const { comments, ...rest } = courierStore.formData_backup;
      console.log(comments)
      Object.assign(courierStore.rmaDeliveryAddress, JSON.parse(JSON.stringify(rest)));
    } else if (newVal === 'new-address') {
      const { comments, ...rest } = courierStore.formData;
      console.log(comments)
      Object.assign(courierStore.rmaDeliveryAddress, JSON.parse(JSON.stringify(rest)));
      courierStore.rmaDeliveryAddress.country = orderDataStore.deliveryAddress.country; // Assign the country

    }
  }
});

watch(
    () => courierStore.selectedCourier, 
    () => {
      noReturnMessage.value = false; 
    }
);
</script>

<template>
  <div class="flex flex-col min-h-screen w-full">
    <div class="flex-grow sm:flex-none">

      <GlobalHeader :on-back="goToProductsPage"/>

      <DescriptionHeader :label="t('howToReturnPage.step2')"/>

      <div v-if="loadingCouriers" class="grid place-content-center mt-10 mb-4">
        <VueSpinner size="20"/>
      </div>
      <div v-else class="mt-10 mb-4">
        <!-- Selected Products -->
        <div class="bg-background-white border overflow-hidden container mt-10 mb-4 mx-auto px-6">
          <div
              v-for="(product, index) in productStore.selectedProducts"
              :key="product.id"
              class="mt-7"
          >
            <ProductItem :product="product" page="HowToReturnCY"/>
            <br>
            <hr v-if="index < productStore.selectedProducts.length-1" class="border-bw-darkLightGrey">
          </div>
        </div>
        <div class="bg-background-white text-bw-lightDarkGrey border overflow-hidden container my-5 py-2 px-6 mx-auto">
          <p class="responsive-title-7 font-semibold">{{ t('howToReturnPage.chooseCourier') }}</p>
          <p class="responsive-body-small italic mt-2">{{ t('howToReturnPage.chooseCourierDesc') }}</p>
        </div>

        <div
            v-for="way in returnWays"
            :key="way.name"
            class="bg-background-white border overflow-hidden container my-5 py-2 px-6 mx-auto"
        >

          <div class="flex justify-between items-center mb-4">
            <label :for="way.name" class="flex-1 flex items-start space-x-2 sm:space-x-8">
              <span class="mt-6 text-bw-black font-bold">
                <PriceFormatter :price="way.returnCost"/>
                </span>
              <span class="flex-1 flex items-start space-x-4">
                <span class="pt-3 responsive-body-medium">
                  <strong class="flex items-center">
                    {{ way.name }}
                  </strong>

                  <span class="text-bw-lightDarkGrey responsive-body-small">{{ t('howToReturnPage.returnWith') }} {{ way.name }}</span>
                </span>
              </span>
            </label>
            <input
                type="radio"
                :id="way.name"
                name="returnhow"
                :value="way.name"
                v-model="courierStore.selectedCourier"
                @change="courierChanged(way)"
                class="mx-2 mt-5 accent-bw-black radio"
                required
            >

          </div>


          <!-- Additional radios for ACS -->
          <div v-if="(way.name === 'ACS CY Express' && courierStore.selectedCourier === 'ACS CY Express')" class="px-4 responsive-body-medium">
            <div class="ml-2 text-bw-black font-bold responsive-body-medium mb-3">{{ t('howToReturnPage.senderInfo') }}</div>
            <div v-if="(way.name === 'ACS CY Express' && courierStore.selectedCourier === 'ACS CY Express')" class="pb-3">
              <div v-if="courierStore.showInfo" :hidden="courierStore.deleteAddress" class="mb-6">
                <label class="flex items-center">
                  <input
                      type="radio"
                      id="edited-address"
                      name="address"
                      value="edited-address"
                      v-model="courierStore.selectedAddress"
                      class="accent-bw-black mt-1 mr-2"
                      checked
                  >
                  <span class="block responsive-body-medium">{{ courierStore.formData_backup.address }}, {{ courierStore.formData_backup.city }}</span>
                  <img src="@/assets/edit_pencil.jpg" alt="Edit Pencil" @click="editForm('edited-address')" class="ml-2 w-6 h-6 cursor-pointer">
                  <img src="@/assets/delete_bin.png" alt="Bin" @click="showPopup=true" class="ml-2 w-3 h-3 cursor-pointer">
                </label>
                <span class="ml-5 text-bw-lightDarkGrey italic responsive-body-small block">
                  {{ courierStore.formData_backup.name }} <br>
                  {{ courierStore.formData_backup.phone }} <br>
                  {{ courierStore.formData_backup.email }}<br>
                  {{ t('howToReturnPage.zipCode') }}: {{ courierStore.formData_backup.zip }} <br>
                </span>

                <!-- Additional Inputs -->
                <form v-if="editingAddress === 'edited-address'" class="sm:mx-6 mt-3 w-1/2 responsive-body-small formwidth">
                  <div class="flex flex-col">
                    <label for="name2" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.fullName') }}</label>
                    <input type="text" v-model="courierStore.formData_backup.name" id="name2" :placeholder="t('howToReturnPage.fullNamePlaceholder')" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <div class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.phone') }}</div>
                    <PhoneInput
                        v-model="courierStore.formData_backup.phone"
                        @update:invalidPhone="invalidPhoneEditedACS = $event"
                    />
                  </div>
                  <div class="flex flex-col">
                    <label for="email2" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.email') }}</label>
                    <input type="email" v-model="courierStore.formData_backup.email" id="email2" placeholder="you@example.com" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <label for="address2" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.address') }}</label>
                    <input type="text" v-model="courierStore.formData_backup.address" id="address2" :placeholder="t('howToReturnPage.addressPlaceholder')" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem]focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <label for="city2" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.city') }}</label>
                    <input type="text" v-model="courierStore.formData_backup.city" id="city2" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <div class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.zip') }}</div>
                    <PostalCodeInput
                        v-model="courierStore.formData_backup.zip"
                        @update:invalidPostCode="invalidPostCodeEditedACS = $event"
                    />
                  </div>
                  <div class="flex flex-col">
                    <label for="comments2" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.comments') }}</label>
                    <textarea id="comments2" v-model="courierStore.formData_backup.comments" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black"></textarea>
                  </div>
                  <span v-if="invalidPhoneEditedACS" class="text-primary-red" :class="{ 'is-invalid': invalidPhoneEditedACS }">{{ t('courier.invalidPhone') }}</span>
                  <span v-if="invalidPostCodeEditedACS" class="text-primary-red" :class="{ 'is-invalid': invalidPostCodeEditedACS }">{{ t('courier.invalidZip') }}</span>
                  <p v-if="v$.formData_backup.$error" class="text-primary-red" :class="{ 'is-invalid': v$.formData_backup.$error }"
                     v-html="v$.formData_backup.$errors[0].$message">
                  </p>
                  <button @click.prevent="displayEditedInfo" class="mt-4 px-4 py-2 bg-black text-bw-white rounded w-full focus:outline-2 focus:outline-offset-2 focus:outline-bw-black" :class="v$.formData_backup.$invalid ? 'bg-bw-darkGrey' : 'bg-bw-black'">
                    {{ t('howToReturnPage.save') }}
                  </button>
                </form>
              </div>
              <div v-if="loadingAddress" class="grid place-content-center">
                <VueSpinner size="20"/>
              </div>
              <div v-if="!loadingAddress" class="mb-6">
                <label class="flex items-center">
                  <input
                      type="radio"
                      id="old-address"
                      name="address"
                      value="old-address"
                      v-model="courierStore.selectedAddress"
                      class="accent-bw-black mt-1 mr-2"
                  >
                  <span class="block responsive-body-medium">{{ orderDataStore.deliveryAddress.address }}, {{ orderDataStore.deliveryAddress.city }}</span>
                </label>
                <span class="ml-5 text-bw-lightDarkGrey italic responsive-body-small block">
                  {{ orderDataStore.deliveryAddress.name }} <br>
                  {{ orderDataStore.deliveryAddress.phone }} <br>
                  {{ orderDataStore.deliveryAddress.email }} <br>
                  {{ t('howToReturnPage.zipCode') }}: {{ orderDataStore.deliveryAddress.zip }} <br>
                  ({{ t('howToReturnPage.orderShippingAddress') }})
                </span>
              </div>
              <div v-if="!loadingAddress">
                <div>
                  <label for="new-address" class="flex responsive-body-medium">
                    <input
                        type="radio"
                        id="new-address"
                        name="address"
                        value="new-address"
                        v-model="courierStore.selectedAddress"
                        @click="courierStore.deleteAddress=false"
                        class="accent-bw-black"
                    >

                    <span class="ml-2">{{ t('howToReturnPage.newSenderInfo') }}</span>
                  </label>
                </div>

                <!-- Additional Inputs -->
                <form v-if="courierStore.selectedAddress === 'new-address'" class="sm:mx-6 mt-3 responsive-body-small w-1/2 formwidth">
                  <div class="flex flex-col">
                    <label for="name" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.fullName') }}</label>
                    <input type="text" v-model="courierStore.formData.name" id="name" :placeholder="t('howToReturnPage.fullNamePlaceholder')" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <div class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.phone') }}</div>
                    <PhoneInput
                        v-model="courierStore.formData.phone"
                        @update:invalidPhone="invalidPhoneNewACS = $event"
                    />
                  </div>
                  <div class="flex flex-col">
                    <label for="email" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.email') }}</label>
                    <input type="email" v-model="courierStore.formData.email" id="email" placeholder="you@example.com" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <label for="address" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.address') }}</label>
                    <input type="text" v-model="courierStore.formData.address" id="address" :placeholder="t('howToReturnPage.addressPlaceholder')" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <label for="city" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.city') }}</label>
                    <input type="text" v-model="courierStore.formData.city" id="city" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black">
                  </div>
                  <div class="flex flex-col">
                    <div class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.zip') }}</div>
                    <PostalCodeInput
                        v-model="courierStore.formData.zip"
                        @update:invalidPostCode="invalidPostCodeNewACS = $event"
                    />
                  </div>
                  <div class="flex flex-col">
                    <label for="comments" class="text-bw-grey font-semibold mt-2">{{ t('howToReturnPage.comments') }}</label>
                    <textarea id="comments" v-model="courierStore.formData.comments" class="border mt-1 w-full pl-[0.3rem] py-[0.3rem] focus:outline-bw-black"></textarea>
                  </div>

                  <span v-if="invalidPhoneNewACS" class="text-primary-red" :class="{ 'is-invalid': invalidPhoneNewACS }">{{ t('courier.invalidPhone') }}</span>
                  <span v-if="invalidPostCodeNewACS" class="text-primary-red" :class="{ 'is-invalid': invalidPostCodeNewACS }">{{ t('courier.invalidZip') }}</span>
                  <p v-if="v$.formData.$error" class="text-primary-red" :class="{ 'is-invalid': v$.formData.$error }" v-html="v$.formData.$errors[0].$message"></p>
                  <button @click.prevent="displayInfo" class="mt-4 px-4 py-2 bg-bw-black text-bw-white rounded w-full focus:outline-2 focus:outline-offset-2 focus:outline-bw-black" :class="v$.formData.$invalid ? 'bg-bw-darkGrey' : 'bg-bw-black'">
                    {{ t('howToReturnPage.save') }}
                  </button>
                </form>

              </div>
            </div>
            <span v-if="v$.selectedAddress.$error" class="text-primary-red responsive-body-small mt-2" :class="{ 'is-invalid': v$.selectedAddress.$error }">{{ v$.selectedAddress.$errors[0].$message }}</span>
            <p v-if="saveMessage" class="text-primary-red responsive-body-small mt-2" :class="{ 'is-invalid': saveMessage }">{{ t('howToReturnPage.saveYourDetails') }}</p>

          </div>
        </div>
      </div>

      <p v-if="v$.selectedCourier.$error" class="text-primary-red text-center responsive-body-small mb-2" :class="{ 'is-invalid': v$.selectedCourier.$error }">
        {{ v$.selectedCourier.$errors[0].$message }}</p>
    </div>
    <p v-if="noReturnMessage" class="text-primary-red text-center responsive-body-small mb-2" :class="{ 'is-invalid': noReturnMessage }" v-html="t('howToReturnPage.noReturnMessage.lowAmount')"></p>

      <div v-if="!loadingCouriers" class="flex sticky md:relative bottom-0 md:bottom-auto bg-background-default justify-center items-center">
        <button
            @click.prevent="goToSummaryPage"
            class="border rounded bg-bw-black text-bw-white responsive-body-large mb-2 py-3.5 px-1 overflow-hidden container hover:bg-bw-darkGrey focus:outline-2 focus:outline-offset-2 focus:outline-bw-black"
            :class="(v$.$invalid || noReturnMessage) ? 'bg-bw-darkGrey' : 'bg-bw-black'"
        >
          {{ t('howToReturnPage.continue') }}
        </button>
      </div>

      <!-- Popup -->
      <div v-if="showPopup" class="fixed inset-0 flex items-center justify-center bg-bw-black bg-opacity-60">
        <div class="relative bg-background-default p-6 rounded-lg overflow-hidden popup">

          <!-- Close (X) Button -->
          <div class="flex justify-between items-center pb-2">
            <h2 class="responsive-title-7 font-bold text-center">{{ t('howToReturnPage.deleteAddressTitle') }}</h2>
            <button
                @click="showPopup = false"
                class="absolute top-2 right-5 text-bw-black hover:text-bw-darkGrey text-xl font-bold">
              &times;
            </button>
          </div>
          <p class="mt-2 text-center responsive-body-medium">{{ t('howToReturnPage.deleteAddressText') }}</p>

          <!-- Buttons -->
          <div class="flex space-x-3 justify-center responsive-body-medium">
            <button @click="deleteData()" class="mt-4 bg-bw-black text-white p-2 w-[50%] rounded hover:bg-bw-darkGrey focus:outline-2 focus:outline-offset-2 focus:outline-bw-black">
              {{ t('howToReturnPage.delete') }}
            </button>
            <button @click="showPopup = false" class="mt-4 bg-bw-black text-white p-2 w-[50%] rounded hover:bg-bw-darkGrey focus:outline-2 focus:outline-offset-2 focus:outline-bw-black">
              {{ t('howToReturnPage.cancel') }}
            </button>
          </div>
        </div>
      </div>
  </div>
</template>

<style scoped>
.radio {
  transform: scale(1.5);
}
@media (min-width: 425px) and (max-width: 553px) {
  .formwidth{
    width: 75%;

  }
}

@media (max-width: 424px){
  .formwidth{
    width: 100%;

  }
}
</style>
